import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  MutableRefObject,
} from "react";
import { createPortal } from "react-dom";
import { debounce } from "lodash";
import { createEditor, jsonFromHTML, type NodeJSON } from "prosekit/core";
import { ProseKit, useDocChange } from "prosekit/react";
import "./prosekit.css";
import { defineExtension } from "./extension";
import { htmlFromMarkdown, markdownFromHTML } from "./markdown";
import Toolbar from "./Toolbar";
import { EditorContextProvider, EditorContextValue } from "./EditorContext";
import TagSelect from "./TagSelect";
import MentionSelect from "./MentionSelect";

type EditorProps = {
  placeholder?: string | undefined;
  enableTixxtMentions?: boolean;
  onChange?(...args: unknown[]): unknown;
  onFocus?(...args: unknown[]): unknown;
  onBlur?(...args: unknown[]): unknown;
  value?: string;
  name?: string;
  readOnly?: boolean;
  id?: string;
  editorRef?: MutableRefObject<any>;
  className?: string;
};

// Silence React v18 warnings for v19 code
const oldConsoleError = console.error;
console.error = function (...data: any[]) {
  try {
    if (
      data[0].includes("Unexpected return value from a callback ref") &&
      data[1].includes("prosekit")
    ) {
      return;
    }
    oldConsoleError.apply(console, data);
  } catch (e) {
    oldConsoleError.apply(console, data);
  }
};

// Default export
const MarkdownEditor = React.memo(function Editor({
  placeholder,
  enableTixxtMentions,
  value,
  onChange,
  readOnly,
  id,
  editorRef,
  className,
}: EditorProps) {
  const [defaultContent, setDefaultContent] = useState<NodeJSON | undefined>();
  const valueRef = useRef<string | null | undefined>(null);

  // (re-)create editor if necessary
  const editor = useMemo(() => {
    const extension = defineExtension({
      readOnly,
      placeholder,
    });
    return createEditor({ extension, defaultContent });
  }, [defaultContent, readOnly, placeholder]);
  if (editorRef) editorRef.current = editor;

  // handle value changes
  useEffect(() => {
    if (valueRef.current !== value) {
      valueRef.current = value;
      if (value) {
        const html = htmlFromMarkdown(value);
        setDefaultContent(jsonFromHTML(html, { schema: editor.schema }));
      } else {
        setDefaultContent(undefined);
      }
    }
  }, [value, editor.schema]);

  // fire onChange debounced
  const debouncedOnChange = useMemo(
    () =>
      debounce(
        () => {
          const html = editor.getDocHTML();
          const markdown = markdownFromHTML(html);

          valueRef.current = markdown;
          onChange && onChange(markdown);
        },
        300,
        { trailing: true },
      ),
    [editor, onChange],
  );
  useDocChange(debouncedOnChange, { editor });

  // Focus editor on label click
  useEffect(() => {
    if (id) {
      const handleClick = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        if (target.getAttribute("for") === id) {
          editor.focus();
        }
      };

      document.addEventListener("click", handleClick);

      return () => {
        document.removeEventListener("click", handleClick);
      };
    }
  }, [id, editor]);

  const context = useMemo(() => ({ readOnly: readOnly || false }), [readOnly]);

  return (
    <MemoizedInnerEditor
      context={context}
      editor={editor}
      id={id}
      className={className}
      onBlur={debouncedOnChange.flush} // important when submit is clicked quickly after entering text
      enableTixxtMentions={enableTixxtMentions}
    />
  );
});

export default MarkdownEditor;

type MemoizedInnerEditorProps = {
  context: EditorContextValue;
  editor: ReturnType<typeof createEditor>;
  id?: string;
  className?: string;
  onBlur?: () => void;
  enableTixxtMentions?: boolean;
};

// This is a nice performance improvement, since we make sure to change the props only when necessary
// If this was inlined in the <Editor />, prosemirror would still be remounted on every change
const MemoizedInnerEditor = React.memo(function MemoizedInnerEditor({
  context,
  editor,
  id,
  className,
  onBlur,
  enableTixxtMentions,
}: MemoizedInnerEditorProps) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);

  // Enhanced context with fullscreen state and toggle function
  const enhancedContext = useMemo(() => {
    const toggleFullscreen = () => {
      const newFullscreenState = !isFullscreen;
      setIsFullscreen(newFullscreenState);

      if (newFullscreenState) {
        dialogRef.current?.showModal();
        handleDialogOpen();
      } else {
        dialogRef.current?.close();
      }
    };

    return {
      ...context,
      isFullscreen,
      toggleFullscreen,
    };
  }, [context, isFullscreen]);

  function handleDialogOpen() {
    setIsFullscreen(true);
    document.body.style.setProperty("overflow", "hidden");
  }

  function handleDialogClose() {
    setIsFullscreen(false);
    document.body.style.removeProperty("overflow");
  }

  // Editor content to render
  const editorContent = (
    <div
      className={`form-control p-0 box-border h-full w-full min-h-36 overflow-y-hidden overflow-x-hidden flex flex-col ${
        className || ""
      }`}
    >
      <Toolbar />
      <div
        className={`relative w-full flex-1 box-border overflow-y-scroll ${
          context.readOnly ? "bg-gray-100" : ""
        }`}
      >
        <div
          ref={editor.mount}
          id={id}
          className="ProseMirror prose box-border min-h-full px-3 py-2 outline-none outline-0"
          onBlur={onBlur}
        />
        {enableTixxtMentions ? (
          <>
            <MentionSelect />
            <TagSelect />
          </>
        ) : null}
      </div>
    </div>
  );

  return (
    <EditorContextProvider value={enhancedContext}>
      <ProseKit editor={editor}>
        {createPortal(
          <dialog
            ref={dialogRef}
            className="tixxt__dialog inset-8 w-auto h-auto rounded m-0 p-0"
            // close when clicking on the backdrop
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                event.currentTarget.close();
              }
            }}
            onClose={handleDialogClose}
          >
            {isFullscreen ? editorContent : null}
          </dialog>,
          document.body,
        )}
        {!isFullscreen ? editorContent : null}
      </ProseKit>
    </EditorContextProvider>
  );
});
